import React from 'react';
import { Upload, FileText, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import type { LessonDTO, LessonTypeConfig } from '../../../core/types/lesson.types';
import { uploadAPI, FileFolder } from '../../../api/upload.client';
import { filesApi } from '../../../../../core/api/files.api';
import { useAuthProviderV2, useRoutesProvider } from '../../../../../providers/provider-hooks';
import { DatePickerInput } from '../../../../../components/forms/raw-inputs/date-picker-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { getErrorStatus } from '@/core/api/response-handler';

interface LessonMetadataProps {
  lesson: LessonDTO | null;
  config: LessonTypeConfig;
  errors: Record<string, any>;
  onChange: (updates: Partial<LessonDTO>) => void;
  isPreview?: boolean;
}

/**
 * Helper to safely format deadline for DatePickerInput component
 * Returns YYYY-MM-DD format string
 * Handles multiple formats: ISO strings, timestamps, bigints, Date objects
 */
const formatDeadlineForInput = (deadline: any): string => {
  if (!deadline) return '';

  try {
    let date: Date;

    // Handle different formats from backend/state
    if (typeof deadline === 'string') {
      // ISO string from backend: "2026-06-15T23:59:59"
      date = new Date(deadline);
    } else if (typeof deadline === 'number') {
      // Legacy number timestamp
      date = new Date(deadline);
    } else if (typeof deadline === 'bigint') {
      // Corrupted bigint timestamp
      date = new Date(Number(deadline));
    } else if (deadline instanceof Date) {
      date = deadline;
    } else {
      console.warn('Unexpected deadline format:', typeof deadline, deadline);
      return '';
    }

    // Validate date is reasonable
    if (isNaN(date.getTime()) || date.getFullYear() < 1900 || date.getFullYear() > 9999) {
      console.warn('Invalid deadline detected, clearing field:', deadline);
      return '';
    }

    // Return YYYY-MM-DD format for DatePickerInput
    return format(date, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting deadline:', error);
    return '';
  }
};

export const LessonMetadata: React.FC<LessonMetadataProps> = ({
  lesson,
  config,
  errors,
  onChange,
  isPreview = false,
}) => {
  const [uploading, setUploading] = React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState<File | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  // Role-based access control for file deletion
  const { userInfo } = useAuthProviderV2();
  const { ROLES } = useRoutesProvider();

  const canDeleteFiles = userInfo?.role && [ROLES.ADMIN, ROLES.DBEST].includes(userInfo.role as any);

  // Iesirea din componenta se face DUPA toate hook-urile: React cere aceeasi ordine de
  // apeluri la fiecare randare. Cu return-ul inaintea lor, orice trecere a lui `lesson`
  // din truthy in falsy arunca "Rendered fewer hooks than expected".
  if (!lesson) return null;

  const handleChange = (field: keyof LessonDTO, value: any) => {
    onChange({ [field]: value });
  };

  // Handle PDF upload
  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = uploadAPI.validatePdf(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setUploadingFile(file);
    setUploading(true);
    try {
      const url = await uploadAPI.uploadPdf(file, FileFolder.LESSON_PDFS);
      handleChange('pdfManualUrl', url);
      toast.success('PDF-ul a fost încărcat cu succes');
    } catch (error: unknown) {
      toast.error('Nu s-a putut încărca PDF-ul');
    } finally {
      setUploading(false);
      setUploadingFile(null);
    }
  };

  // Remove PDF - deletes file from Azure storage
  const handleDeleteClick = () => {
    if (!lesson?.pdfManualUrl) return;
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!lesson?.pdfManualUrl) return;

    setShowDeleteDialog(false);
    setUploading(true);

    try {
      // Delete file from Azure storage
      await filesApi.deleteFile(lesson.pdfManualUrl);

      // Update form state to remove URL
      handleChange('pdfManualUrl', '');

      toast.success('PDF-ul a fost șters cu succes');
    } catch (error: unknown) {
      console.error('Error deleting PDF:', error);

      // Handle specific error cases
      if (getErrorStatus(error) === 403) {
        toast.error('Doar administratorii pot șterge fișiere');
      } else {
        toast.error('Nu s-a putut șterge PDF-ul');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="space-y-6 p-6 bg-card rounded-lg border border-border shadow-sm">
      <div className="flex items-center gap-3 pb-4 border-b">
        <config.icon className="w-6 h-6 text-primary" />
        <div>
          <h2 className="text-xl font-bold text-foreground">
            {config.displayName}
          </h2>
          <p className="text-sm text-muted-foreground">
            Completează detaliile lecției
          </p>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Titlu *
        </label>
        <input
          type="text"
          value={lesson.title || ''}
          onChange={(e: any) => handleChange('title', e.target.value)}
          disabled={isPreview}
          maxLength={config.validation.titleMaxLength}
          placeholder="Introdu titlul lecției..."
          className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-muted"
        />
        {errors?.title && (
          <p className="mt-1 text-sm text-destructive">{errors.title}</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          {(lesson.title?.length || 0)} / {config.validation.titleMaxLength} caractere
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Descriere *
        </label>
        <textarea
          value={lesson.description || ''}
          onChange={(e: any) => handleChange('description', e.target.value)}
          disabled={isPreview}
          maxLength={config.validation.descriptionMaxLength}
          placeholder="Descrie conținutul lecției..."
          rows={4}
          className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-muted"
        />
        {errors?.description && (
          <p className="mt-1 text-sm text-destructive">{errors.description}</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          {(lesson.description?.length || 0)} / {config.validation.descriptionMaxLength} caractere
        </p>
      </div>

      {/* Deadline - only for types that support it */}
      {config.features.hasDeadline && (
        <div>
          <DatePickerInput
            name="deadline"
            label="Termen limită"
            value={formatDeadlineForInput(lesson.deadline)}
            handleChange={(e: any) => {
              const dateString = e.target.value; // YYYY-MM-DD format from DatePickerInput

              console.log('📝 LessonMetadata onChange:');
              console.log('  - received dateString:', dateString);
              console.log('  - dateString type:', typeof dateString);

              if (dateString) {
                // Set to end of day (23:59:59) in local timezone
                const date = new Date(dateString);
                console.log('  - parsed date object:', date);

                date.setHours(23, 59, 59, 0); // No milliseconds for cleaner ISO string
                console.log('  - after setHours:', date);

                // ✅ Send ISO 8601 string (backend expects this format)
                // toISOString() converts to UTC automatically (standard practice)
                const isoString = date.toISOString();
                console.log('  - ISO string output:', isoString);

                handleChange('deadline', isoString);
              } else {
                console.log('  - clearing deadline (undefined)');
                handleChange('deadline', undefined);
              }
            }}
            disabled={isPreview}
            dateFormat="yyyy-MM-dd"
          />
          {errors?.deadline && (
            <p className="mt-1 text-sm text-destructive">{errors.deadline}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            Termenul limită va fi setat la sfârșitul zilei (23:59).
          </p>
        </div>
      )}

      {/* Points - if lesson awards points */}
      {config.features.awardsPoints && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Puncte totale
          </label>
          <input
            type="number"
            value={lesson.punctajMaxim ? Number(lesson.punctajMaxim) : 0}
            onChange={(e: any) => handleChange('punctajMaxim', parseInt(e.target.value, 10) || 0)}
            disabled={isPreview}
            min={0}
            placeholder="0"
            className="w-32 px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-muted"
          />
          {errors?.punctajMaxim && (
            <p className="mt-1 text-sm text-destructive">{errors.punctajMaxim}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            Punctajul total va fi calculat automat din slide-uri
          </p>
        </div>
      )}

      {/* PDF Manual Upload */}
      <div className="space-y-2">
        <label className="block text-sm font-bold text-dark">
          Manual PDF (Opțional)
        </label>

        {/* STATE 1: No PDF, not uploading */}
        {!lesson?.pdfManualUrl && !uploadingFile && (
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-md hover:bg-border cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <span>Încarcă manual PDF</span>
            <input
              type="file"
              accept="application/pdf"
              onChange={handlePdfUpload}
              disabled={uploading || isPreview}
              className="hidden"
            />
          </label>
        )}

        {/* STATE 2: Uploading with filename */}
        {uploadingFile && (
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md border-2 border-blue-500 border-dashed">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="flex-1 text-sm text-foreground truncate">
              {uploadingFile.name}
            </span>
            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
            <span className="text-xs text-muted-foreground">Se încarcă...</span>
          </div>
        )}

        {/* STATE 3: PDF uploaded successfully */}
        {lesson?.pdfManualUrl && !uploadingFile && (
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <FileText className="w-4 h-4 text-primary" />
            <a
              href={lesson.pdfManualUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-sm text-primary hover:underline truncate"
            >
              {lesson.pdfManualUrl.split('/').pop()}
            </a>
            {!isPreview && canDeleteFiles && (
              <button
                onClick={handleDeleteClick}
                disabled={uploading}
                className="p-1 hover:bg-destructive/10 rounded text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
                title="Șterge PDF"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Încarcă un manual PDF pentru această lecție (maxim 10MB)
        </p>
      </div>

      {/* Feature indicators */}
      <div className="pt-4 border-t">
        <div className="flex flex-wrap gap-2">
          {config.features.requiresApproval && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Necesită aprobare
            </span>
          )}
          {config.features.allowsMultipleAttempts && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Multiple încercări permise
            </span>
          )}
          {config.features.awardsPoints && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Acordă puncte
            </span>
          )}
        </div>
      </div>
    </div>

      {/* Delete PDF Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="border-2 border-destructive">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Șterge fișier PDF
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Sigur vrei să ștergi acest PDF? Fișierul va fi eliminat permanent din Azure Storage și această acțiune nu poate fi anulată.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={uploading}
              className="bg-gray-100 hover:bg-gray-200"
            >
              Anulează
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={uploading}
              className="gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Se șterge...
                </>
              ) : (
                <>
                  <X className="w-4 h-4" />
                  Șterge PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LessonMetadata;


